<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import useSharedMethod from "@/use/SharedMethod.js";
import {useTaskModelStore} from "@/stores/Boarding/TaskModel.js";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },

    setup(props) {
        const {mxRules} = useValidation();

        const {loading, dialog, overlay} = useSharedData();

        const {$route, $refs} = useTools();

        const {getBack} = useSharedMethod();

        const taskModelStore = useTaskModelStore();

        const readonly = computed(() => props.viewAction || props.deleteAction);

        const taskModel = ref({});

        function findById(id) {
            loading.value = true;
            axios.get(`/boardingTaskModels/${id}`).then((response) => {
                taskModel.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/boardingTaskModels', taskModel.value).then((response) => {
                    taskModelStore.addTaskModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/boardingTaskModels', taskModel.value).then((response) => {
                    taskModelStore.updateTaskModel(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/boardingTaskModels/${taskModel.value.id}`).then(() => {
                taskModelStore.deleteTaskModel(taskModel.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            taskModel,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        scrollable
        max-width="800"
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text>
                <v-form lazy-validation ref="form">
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="taskModel.code"
                                        entity="BOARDING_TASK_MODEL"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-code-generation>
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="taskModel.name"
                                        auto-focus
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12" md="12">
                                    <w-label :label="$t('common.comment')"/>
                                    <w-simple-text-area
                                        v-model="taskModel.description"
                                        :readonly="readonly"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>